import moment from "moment";
import React, { Component } from "react";
import "react-block-ui/style.css";
import Datetime from "react-datetime";
import { Link } from "react-router-dom";
import LoadingIndicator from "../Components/LoadingIndicator/LoadingIndicator";
import ReportsService from "../utils/ReportsService";
import UsersService from "../utils/UsersService";

const Stat = props => {
  const { number, text, percentage, alert, warning, show_number, success } = props;
  let cname = "panel status panel-info";
  if (alert) cname = "panel status panel-danger";
  if (warning) cname = "panel status panel-warning";
  if (success) cname = "panel status panel-success";

  let heading_class = show_number
    ? "panel-heading"
    : "panel-heading panel-success-heading";
  const number_label = show_number ? `${number || 0}` : "";
  const percentage_label = percentage !== undefined ? `(${percentage}%)` : "";

  return (
    <div className="status-card" style={{ width: 225 }}>
      <div className={cname}>
        <div className={heading_class}>
          <h1 className="panel-title text-center">
            {number_label} {percentage_label}
          </h1>
        </div>
        <div className="panel-body text-center">
          <strong>{text}</strong>
        </div>
      </div>
    </div>
  );
};

// Derives a human-readable result sentence from deal flags + notes
function buildResultText(deal) {
  const { flags, reasons, descriptions } = deal;

  if (flags.includes("on_hold")) {
    const note = reasons.on_hold || descriptions.on_hold || "";
    return "HOLD" + (note ? " - " + note : "");
  }

  if (flags.includes("sale_made")) {
    const note = deal.notes_admin || "";
    return "SALE" + (note ? " - " + note : "");
  }

  if (flags.includes("no_sale")) {
    const reason = reasons.no_sale
      ? reasons.no_sale
          .split("_")
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")
      : "";
    const note = descriptions.no_sale || "";
    let text = "NO SALE";
    if (reason) text += " (" + reason + ")";
    if (note) text += " - " + note;
    return text;
  }

  if (flags.includes("dead_deal")) {
    const reason = reasons.dead_deal
      ? reasons.dead_deal
          .split("_")
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")
      : "";
    const note = descriptions.dead_deal || "";
    let text = "DEAD DEAL";
    if (reason) text += " (" + reason + ")";
    if (note) text += " - " + note;
    return text;
  }

  if (flags.includes("tcs_cancelled")) {
    const note = descriptions.cancelled || "";
    return "CANCELLED SALE" + (note ? " - " + note : "");
  }

  if (flags.includes("tcs_not_presented")) {
    const reason = reasons.tcs_not_presented
      ? reasons.tcs_not_presented
          .split("_")
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")
      : "";
    const note = descriptions.tcs_not_presented || "";
    let text = "NO SHOW";
    if (reason) text += " (" + reason + ")";
    if (note) text += " - " + note;
    return text;
  }

  if (flags.includes("tcs_presented")) {
    const note = descriptions.tcs_presented || "";
    return note ? "PRESENTED - " + note : "PRESENTED - pending outcome";
  }

  return "—";
}

// Extracts first name from an email or full name string
function repFirstName(emailOrName) {
  if (!emailOrName) return "—";
  // If it looks like an email, take the part before @
  if (emailOrName.includes("@")) {
    const local = emailOrName.split("@")[0];
    // Capitalise first letter
    return local.charAt(0).toUpperCase() + local.slice(1);
  }
  // Otherwise return first word
  return emailOrName.split(" ")[0];
}

// Gets status directly from deal flags
function getStatus(deal) {
  const { flags } = deal;

  switch (true) {
    case flags.includes("on_hold"):           return "HOLD";
    case flags.includes("sale_made"):         return "SALE";
    case flags.includes("no_sale"):           return "NO SALE";
    case flags.includes("dead_deal"):         return "DEAD DEAL";
    case flags.includes("tcs_cancelled"):     return "CANCELLED SALE";
    case flags.includes("tcs_not_presented"): return "NO SHOW";
    case flags.includes("tcs_presented"):     return "PRESENTED";
    default:                                  return "—";
  }
}

// Gets rep name from coaches list or falls back to email parsing
function getRepName(email, coaches) {
  if (!email) return "—";
  const coach = coaches.find(c => c.email === email);
  if (coach) return coach.name;
  // Fallback to email parsing
  return repFirstName(email);
}

// Parent counts are stored as words; show them compactly as "attending / household"
// (e.g. "1 / 2" = 1 parent attending from a 2 parent household).
const PARENT_NUM = { one: 1, two: 2 };
function parentsCell(deal) {
  const hh = PARENT_NUM[deal.tcs_parents_in_household] || "—";
  const att = PARENT_NUM[deal.tcs_parents_attending_tcs] || "—";
  return `${att} / ${hh}`;
}

const COLS = { date: 150, rep: 140, deal: 60, parents: 70, status: 100, comments: 470 };
const TABLE_WIDTH =
  COLS.date + COLS.rep + COLS.deal + COLS.parents + COLS.status + COLS.comments;

const DealTable = ({ title, deals, coaches }) => (
  <div style={{ marginBottom: 20 }}>
    <h6 style={{ marginBottom: 8, fontWeight: "bold", fontSize: "16px" }}>
      {title} ({deals.length})
    </h6>
    {deals.length === 0 ? (
      <p style={{ color: "#888", marginBottom: 0 }}>None.</p>
    ) : (
      <table
        className="table table-bordered table-zebra"
        style={{
          marginBottom: 0,
          tableLayout: "fixed",
          width: TABLE_WIDTH,
          fontSize: 15,
          fontWeight: 600,
          color: "#1f2937"
        }}
      >
        <colgroup>
          <col style={{ width: COLS.date }} />
          <col style={{ width: COLS.rep }} />
          <col style={{ width: COLS.deal }} />
          <col style={{ width: COLS.parents }} />
          <col style={{ width: COLS.status }} />
          <col style={{ width: COLS.comments }} />
        </colgroup>
        <thead>
          <tr>
            <th style={{ whiteSpace: "nowrap" }}>Date</th>
            <th>Rep</th>
            <th>Deal #</th>
            <th style={{ whiteSpace: "nowrap" }}>Att / HH</th>
            <th>Status</th>
            <th>Comments</th>
          </tr>
        </thead>
        <tbody>
          {deals.map(deal => (
            <tr key={deal.id}>
              <td style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
                {deal.tcs_at ? moment(deal.tcs_at).format("D/M/YYYY h:mma") : "—"}
              </td>
              <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {getRepName(deal.tcs_assigned_to, coaches)}
              </td>
              <td>
                <Link to={`/m1/deals/${deal.id}`}>{deal.id}</Link>
              </td>
              <td style={{ whiteSpace: "nowrap", overflow: "hidden" }}>{parentsCell(deal)}</td>
              <td>{getStatus(deal)}</td>
              <td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                {buildResultText(deal)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

// Small card summarising a rep's presentation conversion: sales / presented (%).
const RepResultCard = ({ name, sales, presented }) => {
  const pct = presented > 0 ? Math.round((sales / presented) * 100) : 0;
  return (
    <div className="status-card" style={{ width: 200 }}>
      <div className="panel status panel-info">
        <div className="panel-heading">
          <h1 className="panel-title text-center">
            {sales}/{presented} ({pct}%)
          </h1>
        </div>
        <div className="panel-body text-center">
          <strong>{name}</strong>
        </div>
      </div>
    </div>
  );
};

class ReportingDealComments extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filter: {
        start_date: moment().subtract(1, "days").format("DD MMM YYYY"),
        end_date: moment().subtract(1, "days").format("DD MMM YYYY"),
        tcs_assigned_to: ""
      },
      data: null,
      coaches: [],
      loadingInProgress: false
    };
  }

  componentDidMount() {
    this.fetchCoaches();
    this.fetchData();
  }

  fetchCoaches = () => {
    UsersService.allUsers().then(data => {
      this.setState({
        coaches: data
          .filter(c => c.roles && c.roles.includes("tcs_coach"))
          .sort((a, b) => (a.name > b.name ? 1 : -1))
      });
    });
  };

  fetchData = () => {
    this.setState({ loadingInProgress: true });
    ReportsService.salesPersonResults({
      filter: {
        start_date: this.state.filter.start_date,
        end_date: this.state.filter.end_date,
        date_field: "tcs_at",
        include_followup_closes: true
      }
    }).then(data => {
      this.setState({ loadingInProgress: false, data: data });
    });
  };

  handleFilterChange = event => {
    const filter = { ...this.state.filter, [event.target.id]: event.target.value };
    // Rep filter is client-side only — no need to re-fetch
    this.setState({ filter });
  };

  clearFilters = () => {
    const filter = {
      start_date: moment().subtract(1, "days").format("DD MMM YYYY"),
      end_date: moment().subtract(1, "days").format("DD MMM YYYY"),
      tcs_assigned_to: ""
    };
    this.setState({ filter }, this.fetchData);
  };

  dateFilterChanged = (field, value) => {
    const filter = { ...this.state.filter, [field]: value.format("DD MMM YYYY") };
    this.setState({ filter }, this.fetchData);
  };

  render() {
    const { data, filter, coaches, loadingInProgress } = this.state;

    if (!data) {
      return <LoadingIndicator loadingInProgress={loadingInProgress} />;
    }

    // Filter all deals by rep if selected and ensure they have tcs_assigned_to
    const allDeals = data.deals
      .filter(d => d.tcs_assigned_to) // Only deals assigned to reps
      .filter(d => !filter.tcs_assigned_to || d.tcs_assigned_to === filter.tcs_assigned_to);

    const startDay = moment(filter.start_date, "DD MMM YYYY").startOf("day");
    const endDay = moment(filter.end_date, "DD MMM YYYY").endOf("day");
    const scheduledInRange = d =>
      d.tcs_at && moment(d.tcs_at).isBetween(startDay, endDay, null, "[]");

    const presented = [];
    const unpresented = [];
    const followUp = [];
    allDeals.forEach(deal => {
      if (!scheduledInRange(deal)) {
        followUp.push(deal);
      } else if (deal.flags.includes("tcs_presented")) {
        presented.push(deal);
      } else if (
        deal.flags.includes("tcs_not_presented") ||
        deal.flags.includes("tcs_cancelled")
      ) {
        // Only no show and cancelled - NOT dead deals
        unpresented.push(deal);
      }
    });

    // Group each table by rep (all of one rep, then the next), then by date/time.
    const byRepThenDate = (a, b) => {
      const repA = getRepName(a.tcs_assigned_to, coaches);
      const repB = getRepName(b.tcs_assigned_to, coaches);
      if (repA !== repB) return repA.localeCompare(repB);
      const dateA = a.tcs_at ? new Date(a.tcs_at) : 0;
      const dateB = b.tcs_at ? new Date(b.tcs_at) : 0;
      return dateA - dateB;
    };
    [presented, unpresented, followUp].forEach(list => list.sort(byRepThenDate));

    const hasAnyDeals =
      presented.length > 0 || unpresented.length > 0 || followUp.length > 0;

    // Overview counts. A sold deal was also presented, so Presented includes sales
    // (matches the list count). Sale counts the sold presentations.
    const presentedCount = presented.length;
    const noPresCount = unpresented.length;
    const saleCount = presented.filter(d => d.flags.includes("sale_made")).length;

    // Per-rep presentation conversion: sales / presented (%).
    const repStats = {};
    presented.forEach(deal => {
      const name = getRepName(deal.tcs_assigned_to, coaches);
      if (!repStats[name]) repStats[name] = { sales: 0, presented: 0 };
      repStats[name].presented += 1;
      if (deal.flags.includes("sale_made")) repStats[name].sales += 1;
    });
    const repNames = Object.keys(repStats).sort((a, b) => a.localeCompare(b));

    return (
      <LoadingIndicator loadingInProgress={loadingInProgress}>
        <div>
          {/* Filters */}
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ marginTop: 0 }}>Choose a range</h4>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <Datetime
                inputProps={{ className: "form-control input-sm", style: { width: 140 } }}
                onChange={v => this.dateFilterChanged("start_date", v)}
                value={filter.start_date}
                closeOnSelect={true}
                dateFormat="DD MMM YYYY"
                timeFormat={false}
              />
              <Datetime
                inputProps={{ className: "form-control input-sm", style: { width: 140 } }}
                onChange={v => this.dateFilterChanged("end_date", v)}
                value={filter.end_date}
                closeOnSelect={true}
                dateFormat="DD MMM YYYY"
                timeFormat={false}
              />
              <select
                id="tcs_assigned_to"
                className="form-control input-sm"
                style={{ width: 180 }}
                value={filter.tcs_assigned_to}
                onChange={this.handleFilterChange}
              >
                <option value="">(all reps)</option>
                {coaches.map(coach => (
                  <option key={coach.email} value={coach.email}>
                    {coach.name}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-default btn-sm"
                onClick={this.clearFilters}
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Stats Summary */}
          <div style={{ marginBottom: 20 }}>
            <h4>Overview</h4>
            <div style={{ display: "flex", gap: 8 }}>
              <Stat
                number={presentedCount}
                text="Presented"
                show_number={true}
              />
              <Stat
                number={noPresCount}
                text="No Pres"
                show_number={true}
                alert={true}
              />
              <Stat
                number={saleCount}
                text="Sale"
                show_number={true}
                success={true}
              />
            </div>
          </div>

          {/* Deal Comments */}
          <h4 style={{ marginBottom: 20 }}>Deal Comments</h4>

          {!hasAnyDeals ? (
            <p style={{ color: "#888" }}>No deals found for this range.</p>
          ) : (
            <div>
              <DealTable title="Presented" deals={presented} coaches={coaches} />
              <DealTable title="Unpresented" deals={unpresented} coaches={coaches} />
              <DealTable title="Follow Up" deals={followUp} coaches={coaches} />
            </div>
          )}

          {/* Per-rep presentation results: sales / presented (%) */}
          {repNames.length > 0 && (
            <div style={{ marginTop: 25 }}>
              <h4 style={{ marginBottom: 10 }}>Rep Results</h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {repNames.map(name => (
                  <RepResultCard
                    key={name}
                    name={name}
                    sales={repStats[name].sales}
                    presented={repStats[name].presented}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </LoadingIndicator>
    );
  }
}

export default ReportingDealComments;
